import { useState, useCallback } from 'react';
import axios from 'axios';

const useQuailHQData = ({ getAuthHeaders, apiBackend, setError, showSuccessMessage }) => {
  const [isRequestingBarcodes, setIsRequestingBarcodes] = useState(false);
  const [quailhqStatuses, setQuailhqStatuses] = useState({});
  const [isLoadingQuailHQStatus, setIsLoadingQuailHQStatus] = useState(false);

  const quailhqUrl = `${apiBackend}/api/quailhq`;

  const fetchQuailHQStatus = useCallback(async (channelIds) => {
    if (!channelIds || channelIds.length === 0) return;
    setIsLoadingQuailHQStatus(true);
    try {
      const config = await getAuthHeaders();
      const results = await Promise.allSettled(
        channelIds.map(id =>
          axios.get(`${quailhqUrl}/${id}/status`, config).then(res => ({
            channelId: id,
            ...res.data.data,
          }))
        )
      );
      const statuses = {};
      results.forEach((result) => {
        if (result.status === 'fulfilled') {
          const { channelId, ...status } = result.value;
          statuses[channelId] = status;
        }
      });
      setQuailhqStatuses(statuses);
    } catch (err) {
      console.error('Error fetching QuailHQ statuses:', err);
    } finally {
      setIsLoadingQuailHQStatus(false);
    }
  }, [quailhqUrl, getAuthHeaders]);

  const requestBarcodes = useCallback(async (channelId, itemIds) => {
    if (!channelId || !itemIds || itemIds.length === 0) return null;
    setIsRequestingBarcodes(true);
    try {
      const config = await getAuthHeaders();
      const response = await axios.post(
        `${quailhqUrl}/${channelId}/barcodes`,
        { itemIds },
        config
      );
      if (response.data.success) {
        const result = response.data.data;
        const count = result.assigned || 0;
        showSuccessMessage?.(`Assigned ${count} QuailHQ barcode${count !== 1 ? 's' : ''}`);
        return result;
      }
      return null;
    } catch (err) {
      console.error('Error requesting QuailHQ barcodes:', err);
      setError?.(err.response?.data?.error || 'Failed to request QuailHQ barcodes');
      return null;
    } finally {
      setIsRequestingBarcodes(false);
    }
  }, [quailhqUrl, getAuthHeaders, setError, showSuccessMessage]);

  return {
    isRequestingBarcodes,
    quailhqStatuses,
    isLoadingQuailHQStatus,
    fetchQuailHQStatus,
    requestBarcodes,
  };
};

export default useQuailHQData;
